import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { KPI_TOOLTIP_DELAY } from "@/modules/boxDashboard/modules/KPI/components/kpiBlock"
import { VariationBadge } from "@/modules/boxDashboard/modules/KPI/components/variationBadge"
import type {
  IncomeBarModel,
  IncomeKpiModel,
  MrrModel,
  ShopSalesModel,
} from "@/modules/boxDashboard/modules/KPI/modules/income/models/incomeKpiModel"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"

type IncomeKpiBlockProps = {
  model: IncomeKpiModel
  currency: string
  locale: string
}

export function IncomeKpiBlock({ model, currency, locale }: IncomeKpiBlockProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div className="flex flex-col gap-4">
      <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
        {word("boxDashboard.kpi.income.title")}
      </Text>

      <IncomeBarBlock incomeBar={model.incomeBar} formatCurrency={formatCurrency} />

      <div className="flex gap-4">
        <MrrBlock mrr={model.mrr} formatCurrency={formatCurrency} />
        <ShopSalesBlock shopSales={model.shopSales} formatCurrency={formatCurrency} />
      </div>
    </div>
  )
}

type IncomeBarBlockProps = {
  incomeBar: IncomeBarModel
  formatCurrency: (amount: number) => string
}

function IncomeBarBlock({ incomeBar, formatCurrency }: IncomeBarBlockProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-col gap-3 rounded-xl bg-surface-background p-4">
              <div className="flex flex-col gap-3">
                <FocusBar
                  amount={formatCurrency(incomeBar.focusAmount)}
                  monthLabel={incomeBar.focusMonthLabel}
                  progressPercent={incomeBar.focusProgressPercent}
                  variationPercent={incomeBar.variationPercent}
                  variationDirection={incomeBar.variationDirection}
                  hasComparison={incomeBar.hasComparison}
                />

                {incomeBar.showComparisonBar &&
                  incomeBar.comparisonAmount !== null &&
                  incomeBar.comparisonMonthLabel !== null && (
                    <ComparisonBar
                      amount={formatCurrency(incomeBar.comparisonAmount)}
                      monthLabel={incomeBar.comparisonMonthLabel}
                      progressPercent={incomeBar.comparisonProgressPercent}
                    />
                  )}
              </div>
            </div>
          }
        />

        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {word("boxDashboard.kpi.income.tooltip")}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type MrrBlockProps = {
  mrr: MrrModel
  formatCurrency: (amount: number) => string
}

function MrrBlock({ mrr, formatCurrency }: MrrBlockProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-1 flex-col gap-2 rounded-xl bg-surface-background p-4">
              <div className="flex items-center gap-2">
                <Text fontStyle="LM" className="font-semibold text-textOnSurface-muted">
                  {word("boxDashboard.kpi.income.mrr.title")}
                </Text>
              </div>

              <div className="flex items-center gap-2">
                <Text fontStyle="HM" className="text-textOnSurface-default">
                  {mrr.amount === 0 && mrr.variationPercent === null ? "–" : formatCurrency(mrr.amount)}
                </Text>
                {mrr.variationPercent !== null && mrr.variationDirection !== null && (
                  <VariationBadge
                    value={mrr.variationPercent}
                    direction={mrr.variationDirection}
                    type="percent"
                    size="size-6"
                    className="text-textOnSurface-muted"
                    fontStyle="LM"
                  />
                )}
              </div>
            </div>
          }
        />
        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {word("boxDashboard.kpi.income.mrr.tooltip")}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type ShopSalesBlockProps = {
  shopSales: ShopSalesModel
  formatCurrency: (amount: number) => string
}

function ShopSalesBlock({ shopSales, formatCurrency }: ShopSalesBlockProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-1 flex-col justify-center gap-2 rounded-xl bg-surface-background p-4">
              <div className="flex items-center gap-2">
                <Text fontStyle="LM" className="font-semibold text-textOnSurface-muted">
                  {word("boxDashboard.kpi.income.shopSales.title")}
                </Text>
              </div>

              <div className="flex items-center gap-2">
                <Text fontStyle="HM" className="text-textOnSurface-default">
                  {shopSales.amount === 0 && shopSales.variationPercent === null
                    ? "–"
                    : formatCurrency(shopSales.amount)}
                </Text>
                {shopSales.variationPercent !== null && shopSales.variationDirection !== null && (
                  <VariationBadge
                    value={shopSales.variationPercent}
                    direction={shopSales.variationDirection}
                    type="percent"
                    size="size-6"
                    className="text-textOnSurface-muted"
                    fontStyle="LM"
                  />
                )}
              </div>
            </div>
          }
        />
        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {word("boxDashboard.kpi.income.shopSales.tooltip")}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type FocusBarProps = {
  amount: string
  monthLabel: string
  progressPercent: number
  variationPercent: number | null
  variationDirection: "up" | "down" | "neutral" | null
  hasComparison: boolean
}

function FocusBar({
  amount,
  monthLabel,
  progressPercent,
  variationPercent,
  variationDirection,
  hasComparison,
}: FocusBarProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Text fontStyle="LL" className="text-textOnSurface-default">
          {progressPercent === 0 && !hasComparison ? "–" : amount}
        </Text>
        <Text fontStyle="LS" className="text-textOnSurface-subtle">
          {monthLabel}
        </Text>
        {hasComparison && variationPercent !== null && variationDirection !== null && (
          <VariationBadge value={variationPercent} direction={variationDirection} type="percent" />
        )}
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-surface-background">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-300",
            progressPercent === 0 && !hasComparison
              ? "bg-surface-background-primary-default/20"
              : "bg-surface-background-primary-default",
          )}
          style={{ width: progressPercent === 0 && !hasComparison ? "100%" : `${progressPercent}%` }}
        />
      </div>
    </div>
  )
}

type ComparisonBarProps = {
  amount: string
  monthLabel: string
  progressPercent: number
}

function ComparisonBar({ amount, monthLabel, progressPercent }: ComparisonBarProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Text fontStyle="LL" className="text-textOnSurface-subtle">
          {amount}
        </Text>
        <Text fontStyle="LS" className="text-textOnSurface-subtle">
          {monthLabel}
        </Text>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-surface-background">
        <div
          className="h-full rounded-full bg-interactive-background-primary-faded-highlighted transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  )
}
